import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Loader2, CreditCard, Calendar, Users, Home, Clock, CheckCircle, 
  AlertTriangle, MapPin, Phone, Mail, ChevronRight, Shield, Smartphone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const GuestPaymentPortal = () => {
  const { bookingToken } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [booking, setBooking] = useState(null);
  const [property, setProperty] = useState(null);
  const [host, setHost] = useState(null);
  const [isPaid, setIsPaid] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [paymentRequired, setPaymentRequired] = useState(false);
  
  // Registration form
  const [showRegister, setShowRegister] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });
  const [registering, setRegistering] = useState(false);
  
  // Payment state
  const [paymentMethod, setPaymentMethod] = useState('mpesa'); // Default to M-Pesa for Kenya
  const [mpesaPhone, setMpesaPhone] = useState('');
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [mpesaCheckoutId, setMpesaCheckoutId] = useState(null);
  const [mpesaStatus, setMpesaStatus] = useState(null);
  const [pollingPayment, setPollingPayment] = useState(false);

  useEffect(() => {
    fetchBooking();
    
    // Check for payment callback
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      setPaymentSuccess(true);
      // Refresh booking data
      setTimeout(fetchBooking, 2000);
    }
  }, [bookingToken]);

  const fetchBooking = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/guest-booking/portal/${bookingToken}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Booking not found');
      }
      const data = await res.json();
      setBooking(data.booking);
      setProperty(data.property);
      setHost(data.host);
      setIsPaid(data.is_paid);
      setIsExpired(data.is_expired);
      setPaymentRequired(data.payment_required);
      
      // Pre-fill registration form
      if (data.booking) {
        setRegisterForm(prev => ({
          ...prev,
          name: data.booking.guest_name || '',
          email: data.booking.guest_email || ''
        }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterAndPay = async () => {
    if (!registerForm.name || !registerForm.email || !registerForm.password) {
      alert('Please fill in all required fields');
      return;
    }
    
    setRegistering(true);
    try {
      const res = await fetch(`${API_URL}/api/guest-booking/portal/${bookingToken}/register-and-pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerForm)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Registration failed');
      }
      
      const data = await res.json();
      // Store guest token
      localStorage.setItem('guest_token', data.token);
      localStorage.setItem('guest', JSON.stringify(data.guest));
      
      setShowRegister(false);
      // Now proceed to payment
      handlePayment();
    } catch (err) {
      alert(err.message);
    } finally {
      setRegistering(false);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    
    try {
      if (paymentMethod === 'stripe') {
        const res = await fetch(`${API_URL}/api/guest-booking/portal/${bookingToken}/pay/stripe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Payment failed');
        }
        
        const data = await res.json();
        // Redirect to Stripe checkout
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
      } else if (paymentMethod === 'mpesa') {
        const phoneToUse = mpesaPhone || booking?.guest_phone;
        if (!phoneToUse) {
          alert('Please enter your M-Pesa phone number');
          setProcessing(false);
          return;
        }
        
        const res = await fetch(`${API_URL}/api/guest-booking/portal/${bookingToken}/pay/mpesa?phone_number=${encodeURIComponent(phoneToUse)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        
        const data = await res.json();
        
        if (!res.ok) {
          throw new Error(data.detail || 'Payment failed');
        }
        
        if (data.success) {
          // STK push sent - start polling
          setMpesaCheckoutId(data.checkout_request_id);
          setMpesaStatus({ status: 'initiated', message: data.message });
          startMpesaPolling(data.checkout_request_id);
        } else {
          // STK push failed - show fallback
          setMpesaStatus({ 
            status: 'fallback', 
            error: data.error,
            fallback: data.fallback 
          });
        }
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const startMpesaPolling = (checkoutId) => {
    setPollingPayment(true);
    let pollCount = 0;
    const maxPolls = 24; // 2 minutes (5 sec intervals)
    
    const poll = async () => {
      pollCount++;
      try {
        const res = await fetch(`${API_URL}/api/guest-booking/mpesa/status/${checkoutId}`);
        const data = await res.json();
        
        if (data.payment_status === 'completed') {
          setMpesaStatus({ 
            status: 'completed', 
            message: 'Payment successful!',
            receipt: data.mpesa_receipt 
          });
          setPollingPayment(false);
          setPaymentSuccess(true);
          // Refresh booking
          setTimeout(fetchBooking, 1000);
          return;
        } else if (data.payment_status === 'failed') {
          setMpesaStatus({ 
            status: 'failed', 
            message: 'Payment was cancelled or failed. Please try again.' 
          });
          setPollingPayment(false);
          return;
        }
        
        // Continue polling
        if (pollCount < maxPolls) {
          setTimeout(poll, 5000);
        } else {
          setMpesaStatus({ 
            status: 'timeout', 
            message: 'Payment confirmation timed out. If you completed the payment, please wait or contact support.' 
          });
          setPollingPayment(false);
        }
      } catch (err) {
        console.error('Poll error:', err);
        if (pollCount < maxPolls) {
          setTimeout(poll, 5000);
        }
      }
    };
    
    setTimeout(poll, 5000); // First poll after 5 seconds
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    try {
      const date = new Date(isoStr);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  const calculateNights = () => {
    if (!booking?.check_in || !booking?.check_out) return 0;
    const start = new Date(booking.check_in);
    const end = new Date(booking.check_out);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-pink-500 mx-auto mb-4" />
          <p className="text-slate-600">Loading your booking...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Booking Not Found</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <p className="text-sm text-slate-500">
            This link may have expired or the booking may have been cancelled.
          </p>
        </div>
      </div>
    );
  }

  if (paymentSuccess || isPaid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Booking Confirmed!</h1>
          <p className="text-slate-600 mb-6">
            Your payment was successful. You will receive a confirmation email with property details and check-in instructions.
          </p>
          
          {booking && (
            <div className="bg-slate-50 rounded-xl p-4 text-left mb-6">
              <h3 className="font-semibold text-slate-800 mb-3">{property?.name}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Check-in</span>
                  <span className="font-medium">{booking.check_in}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Check-out</span>
                  <span className="font-medium">{booking.check_out}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Confirmation</span>
                  <span className="font-medium text-emerald-600">{booking.booking_id}</span>
                </div>
              </div>
            </div>
          )}
          
          <p className="text-xs text-slate-500">
            Check your email for the full confirmation with address and check-in instructions.
          </p>
        </div>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-amber-500" />
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Booking Expired</h1>
          <p className="text-slate-600 mb-6">
            This booking has expired because payment was not completed in time.
          </p>
          <p className="text-sm text-slate-500">
            Please contact the property host to make a new reservation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-500 to-rose-500 text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center gap-2 text-white/80 text-sm mb-2">
            <Shield className="w-4 h-4" />
            Secure Payment Portal
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold">Complete Your Booking</h1>
          {property && <p className="mt-1 text-white/90">{property.name}</p>}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Booking Summary - Sidebar */}
          <div className="lg:col-span-1 lg:order-2">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-4">
              {property?.image_url && (
                <img 
                  src={property.image_url} 
                  alt={property.name}
                  className="w-full h-40 object-cover rounded-xl mb-4"
                />
              )}
              
              <h3 className="font-bold text-lg text-slate-800 mb-1">{property?.name}</h3>
              {property && (
                <p className="text-slate-500 text-sm flex items-center gap-1 mb-4">
                  <MapPin className="w-3.5 h-3.5" />
                  {property.city}, {property.country}
                </p>
              )}
              
              <div className="border-t border-slate-100 pt-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-pink-50 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-pink-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Check-in</p>
                    <p className="font-semibold text-slate-800">{booking?.check_in}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Check-out</p>
                    <p className="font-semibold text-slate-800">{booking?.check_out}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Guests</p>
                    <p className="font-semibold text-slate-800">{booking?.num_guests} guest(s)</p>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-slate-100 mt-4 pt-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-slate-500">{calculateNights()} night(s)</span>
                  <span className="text-slate-800">{property?.currency || 'KES'} {booking?.total_price?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center font-bold text-lg mt-3 pt-3 border-t border-slate-100">
                  <span>Total</span>
                  <span className="text-pink-500">{property?.currency || 'KES'} {booking?.total_price?.toLocaleString()}</span>
                </div>
              </div>
              
              {booking?.expires_at && (
                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-xs text-amber-700 flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    Payment deadline: {formatTime(booking.expires_at)}
                  </p>
                </div>
              )}
              
              {booking?.status === 'waitlist' && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-700">
                    <strong>Waitlist:</strong> Another booking exists for these dates. First to pay gets confirmed.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Payment Form - Main Content */}
          <div className="lg:col-span-2 lg:order-1 space-y-6">
            {/* Guest Information */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-pink-500" />
                Guest Information
              </h2>
              
              {!showRegister ? (
                <div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-slate-500">Name</Label>
                      <p className="font-semibold text-slate-800">{booking?.guest_name}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-500">Email</Label>
                      <p className="font-semibold text-slate-800">{booking?.guest_email || '-'}</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowRegister(true)}
                  >
                    Create Account to Pay
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600 mb-4">
                    Create a guest account to complete your payment and manage your booking.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Full Name *</Label>
                      <Input 
                        value={registerForm.name}
                        onChange={e => setRegisterForm(prev => ({ ...prev, name: e.target.value }))}
                        className="mt-1"
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Email *</Label>
                      <Input 
                        type="email"
                        value={registerForm.email}
                        onChange={e => setRegisterForm(prev => ({ ...prev, email: e.target.value }))}
                        className="mt-1"
                        placeholder="john@example.com"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Phone</Label>
                      <Input 
                        value={registerForm.phone}
                        onChange={e => setRegisterForm(prev => ({ ...prev, phone: e.target.value }))}
                        className="mt-1"
                        placeholder="+254 700 123 456"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Password *</Label>
                      <Input 
                        type="password"
                        value={registerForm.password}
                        onChange={e => setRegisterForm(prev => ({ ...prev, password: e.target.value }))}
                        className="mt-1"
                        placeholder="Create a password"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={handleRegisterAndPay}
                      disabled={registering}
                      className="bg-pink-500 hover:bg-pink-600"
                    >
                      {registering ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Create Account & Pay
                    </Button>
                    <Button variant="ghost" onClick={() => setShowRegister(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>

            {/* Payment Method */}
            {!showRegister && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-pink-500" />
                  Payment Method
                </h2>
                
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  <button
                    onClick={() => setPaymentMethod('stripe')}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      paymentMethod === 'stripe' 
                        ? 'border-pink-500 bg-pink-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <CreditCard className={`w-6 h-6 ${paymentMethod === 'stripe' ? 'text-pink-500' : 'text-slate-400'}`} />
                      <div>
                        <p className="font-semibold text-slate-800">Card Payment</p>
                        <p className="text-xs text-slate-500">Visa, Mastercard, etc.</p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setPaymentMethod('mpesa')}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      paymentMethod === 'mpesa' 
                        ? 'border-emerald-500 bg-emerald-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Smartphone className={`w-6 h-6 ${paymentMethod === 'mpesa' ? 'text-emerald-500' : 'text-slate-400'}`} />
                      <div>
                        <p className="font-semibold text-slate-800">M-Pesa</p>
                        <p className="text-xs text-slate-500">Mobile Money</p>
                      </div>
                    </div>
                  </button>
                </div>
                
                {paymentMethod === 'mpesa' && (
                  <div className="mb-6 space-y-3">
                    <div>
                      <Label className="text-sm font-medium">M-Pesa Phone Number</Label>
                      <Input 
                        value={mpesaPhone || booking?.guest_phone || ''}
                        onChange={e => setMpesaPhone(e.target.value)}
                        className="mt-1"
                        placeholder="0712345678"
                        data-testid="mpesa-phone-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">Enter the phone number to receive the STK push</p>
                    </div>
                    
                    {/* M-Pesa Status Display */}
                    {mpesaStatus && (
                      <div className={`p-4 rounded-lg ${
                        mpesaStatus.status === 'completed' ? 'bg-green-50 border border-green-200' :
                        mpesaStatus.status === 'failed' || mpesaStatus.status === 'timeout' ? 'bg-red-50 border border-red-200' :
                        mpesaStatus.status === 'fallback' ? 'bg-amber-50 border border-amber-200' :
                        'bg-blue-50 border border-blue-200'
                      }`}>
                        {mpesaStatus.status === 'initiated' && (
                          <div className="flex items-center gap-3">
                            <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                            <div>
                              <p className="font-medium text-blue-800">STK Push Sent</p>
                              <p className="text-sm text-blue-600">{mpesaStatus.message}</p>
                              <p className="text-xs text-blue-500 mt-1">Check your phone and enter your M-Pesa PIN</p>
                            </div>
                          </div>
                        )}
                        
                        {mpesaStatus.status === 'completed' && (
                          <div className="flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            <div>
                              <p className="font-medium text-green-800">Payment Successful!</p>
                              {mpesaStatus.receipt && (
                                <p className="text-sm text-green-600">Receipt: {mpesaStatus.receipt}</p>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {(mpesaStatus.status === 'failed' || mpesaStatus.status === 'timeout') && (
                          <div className="flex items-center gap-3">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                            <div>
                              <p className="font-medium text-red-800">Payment Issue</p>
                              <p className="text-sm text-red-600">{mpesaStatus.message}</p>
                            </div>
                          </div>
                        )}
                        
                        {mpesaStatus.status === 'fallback' && mpesaStatus.fallback && (
                          <div>
                            <p className="font-medium text-amber-800 mb-2">STK Push failed. Pay manually:</p>
                            <div className="bg-white rounded p-3 space-y-1">
                              <p className="text-sm"><strong>Paybill:</strong> {mpesaStatus.fallback.paybill}</p>
                              <p className="text-sm"><strong>Account:</strong> {mpesaStatus.fallback.account}</p>
                              <p className="text-sm"><strong>Amount:</strong> KES {mpesaStatus.fallback.amount?.toLocaleString()}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {pollingPayment && (
                      <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Waiting for payment confirmation...
                      </div>
                    )}
                  </div>
                )}
                
                <Button
                  onClick={handlePayment}
                  disabled={processing || pollingPayment || paymentSuccess}
                  className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white py-6 text-lg rounded-xl disabled:opacity-50"
                  data-testid="pay-now-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : pollingPayment ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Waiting for confirmation...
                    </>
                  ) : paymentSuccess ? (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Payment Complete!
                    </>
                  ) : (
                    <>
                      {paymentMethod === 'mpesa' ? 'Pay with M-Pesa' : 'Pay'} {property?.currency || 'KES'} {booking?.total_price?.toLocaleString()}
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
                
                <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
                  <Shield className="w-4 h-4" />
                  {paymentMethod === 'mpesa' ? 'Secure M-Pesa STK Push' : 'Secure payment powered by Stripe'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestPaymentPortal;
